import React, { useState, useEffect, forwardRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { containsEmoji, EMOJI_REGEX } from '../../lib/passwordValidator';

/**
 * Reusable PasswordInput component with accessible Show/Hide toggle
 * and proactive emoji rejection.
 * - Default: hidden (type="password")
 * - Clicking eye button toggles to visible (type="text")
 * - Accessible with aria-label, keyboard navigation, type="button" to prevent form submit
 * - Proactively intercepts and blocks emojis from being entered into password fields
 * - Does not modify or re-encode valid password characters (normal ASCII symbols are preserved)
 */
const PasswordInput = forwardRef(({
  id,
  name,
  value = '',
  onChange,
  onEmojiAttempt,
  placeholder = '••••••••',
  className = '',
  disabled = false,
  autoComplete = 'current-password',
  required = false,
  error = false,
  preventEmoji = true,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [emojiWarning, setEmojiWarning] = useState(false);

  // Auto-dismiss emoji warning after 4 seconds
  useEffect(() => {
    if (emojiWarning) {
      const timer = setTimeout(() => setEmojiWarning(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [emojiWarning]);

  const toggleVisibility = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPassword((prev) => !prev);
  };

  const handleInputChange = (e) => {
    const rawVal = e.target.value;

    if (preventEmoji && containsEmoji(rawVal)) {
      // Prevent the emoji from being accepted into the password field
      const strippedVal = rawVal.replace(new RegExp(EMOJI_REGEX.source, 'gu'), '');
      setEmojiWarning(true);
      if (onEmojiAttempt) {
        onEmojiAttempt('Emojis are not allowed in passwords.');
      }
      // Update target value to stripped version
      e.target.value = strippedVal;
      onChange?.(e);
      return;
    }

    if (emojiWarning) {
      setEmojiWarning(false);
    }
    onChange?.(e);
  };

  return (
    <div className="password-input-container">
      <div className="password-input-wrapper">
        <input
          ref={ref}
          id={id}
          name={name}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          required={required}
          className={`auth-input password-input ${error || emojiWarning ? 'error' : ''} ${className}`.trim()}
          {...props}
        />
        <button
          type="button"
          className="password-toggle-btn"
          onClick={toggleVisibility}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          title={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={0}
        >
          {showPassword ? (
            <EyeOff size={18} className="password-toggle-icon" aria-hidden="true" />
          ) : (
            <Eye size={18} className="password-toggle-icon" aria-hidden="true" />
          )}
        </button>
      </div>
      {emojiWarning && (
        <span className="auth-error-msg auth-emoji-warning" role="alert" style={{ display: 'block', marginTop: '0.35rem' }}>
          Emojis are not allowed in passwords.
        </span>
      )}
    </div>
  );
});

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
